import { ArrowLeft, MessageSquare, Send } from 'lucide-react';
import { useState } from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { useInquiryDetail, useCreateComment, TYPE_CONFIG, STATUS_CONFIG } from '@/features/support';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { AnimatedContainer } from '@/shared/ui/animated';
import { AppLayout } from '../-components/app-layout';

export const Route = createFileRoute('/_authenticated/$mallId/support/$inquiryId')({
  component: InquiryDetailPage,
});

function InquiryDetailPage() {
  const { mallId, inquiryId } = useParams({ from: '/_authenticated/$mallId/support/$inquiryId' });
  const { data, isLoading } = useInquiryDetail(inquiryId!);
  const { mutate: createComment, isPending: isCommenting } = useCreateComment();
  const [commentText, setCommentText] = useState('');

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    createComment(
      { inquiryId: inquiryId!, content: commentText },
      { onSuccess: () => setCommentText('') },
    );
  };

  if (isLoading) {
    return (
      <AppLayout pageTitle="문의 상세">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
      </AppLayout>
    );
  }

  if (!data) {
    return (
      <AppLayout pageTitle="문의 상세">
        <Card className="rounded-2xl">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <MessageSquare className="w-12 h-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">문의를 찾을 수 없어요</p>
          </CardContent>
        </Card>
      </AppLayout>
    );
  }

  const { inquiry, comments } = data;
  const typeConfig = TYPE_CONFIG[inquiry.type];

  return (
    <AppLayout pageTitle="문의 상세">
      <AnimatedContainer>
        <div className="max-w-3xl mx-auto space-y-6">
          {/* 뒤로가기 */}
          <Link
            to="/$mallId/support"
            params={{ mallId: mallId }}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            목록으로
          </Link>

          {/* 문의 헤더 */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${typeConfig.className}`}
                    >
                      <span className="text-[10px]">{typeConfig.icon}</span>
                      {typeConfig.label}
                    </span>
                    <Badge variant={STATUS_CONFIG[inquiry.status].variant}>
                      {STATUS_CONFIG[inquiry.status].label}
                    </Badge>
                  </div>
                  <h1 className="text-lg font-bold">{inquiry.title}</h1>
                  <p className="text-xs text-muted-foreground mt-1 tabular-nums">
                    {new Date(inquiry.createdAt).toLocaleString('ko-KR')}
                  </p>
                </div>
              </div>

              {/* 본문 */}
              <div className="mt-5 pt-5 border-t border-border/30">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{inquiry.content}</p>
              </div>
            </CardContent>
          </Card>

          {/* 댓글 스레드 */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4" />
              답변 및 댓글 ({comments.length})
            </h2>

            {comments.length === 0 ? (
              <Card className="rounded-2xl">
                <CardContent className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">아직 답변이 없어요</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    빠른 시일 내에 답변드리겠습니다
                  </p>
                </CardContent>
              </Card>
            ) : (
              comments.map((comment) => {
                const isAdmin = comment.authorType === 'admin';
                return (
                  <Card
                    key={comment.id}
                    className={`rounded-2xl ${isAdmin ? 'bg-primary-bg/50' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            isAdmin
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {isAdmin ? '관리자' : '사용자'}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {new Date(comment.createdAt).toLocaleString('ko-KR')}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {comment.content}
                      </p>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* 댓글 입력 */}
          <Card className="rounded-2xl">
            <CardContent className="p-4">
              <form onSubmit={handleSubmitComment} className="flex gap-3">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="댓글을 입력하세요..."
                  rows={2}
                  className="flex-1 border-none bg-muted rounded-xl px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary resize-none"
                />
                <Button
                  type="submit"
                  disabled={isCommenting || !commentText.trim()}
                  className="self-end rounded-xl active:scale-95 transition-transform"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </AnimatedContainer>
    </AppLayout>
  );
}
